/**
 * Threat Analysis Tab — rule-based "what should the operator look at?"
 *
 * Previous version called the Python analytics service for ML-driven
 * anomaly scores (Isolation Forest). Operators wanted explicit rules
 * they can reason about, not a black-box score. So this tab is now a
 * deterministic view over the alerts table:
 *
 *   Flight is flagged if it has any of:
 *     • Above the FAA Facility Map ceiling
 *         → AIRSPACE_INTRUSION alerts with metadata.subtype === 'CEILING'
 *     • Breached a defined geofence
 *         → GEOFENCE_BREACH alerts of any severity
 *     • Near critical infrastructure
 *         → not yet defined; placeholder until the operator nails down
 *           the data model. Today this rule never matches.
 *
 * One card per flagged track. The card lists every rule that fired with
 * its severity + time, and links into the Investigation tab for the
 * drill-down.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MapIcon,
  BuildingOffice2Icon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { format, formatDistanceToNow } from 'date-fns';
import { reportsApi, type LatestAlertRow } from '../../services/reportsApi';
import { tracksApi } from '../../services/api';
import type { TimeRange } from '../../services/analyticsService';
import { SkeletonCard } from '../common/Skeleton';

// Same helper as InvestigationTab — show "DJI Mavic" not "DUMP-abc123".
function deviceLabel(t: any): string {
  const parts = [t?.manufacturer, t?.model].filter(Boolean);
  if (parts.length > 0) return parts.join(' ');
  if (t?.classification) return String(t.classification).replace(/_/g, ' ');
  return 'Unknown';
}

// Severity rank for sorting "worst first."
const SEVERITY_RANK: Record<string, number> = { CRITICAL: 3, WARNING: 2, INFO: 1 };

type RuleKey = 'CEILING' | 'GEOFENCE' | 'CRITICAL_INFRA';

interface RuleHit {
  rule: RuleKey;
  severity: LatestAlertRow['severity'];
  title: string;
  createdAt: string;
  alertId: string;
}

interface FlaggedTrack {
  trackId: string;
  hits: RuleHit[];
  // Highest severity across hits; drives the card's color treatment.
  topSeverity: LatestAlertRow['severity'];
  // Most-recent alert across hits; drives the "X minutes ago" line.
  lastSeenIso: string;
}

interface ThreatTabProps {
  tenantId: string;
  timeRange: TimeRange;
  startTime: string;
  endTime: string;
  // Kept for API compatibility with AnalyticsPage but no longer used —
  // this tab no longer depends on the Python analytics service.
  analyticsAvailable: boolean | null;
  onInvestigateTrack: (trackId: string) => void;
}

// Map an alert row to a rule classification. Returns null when the alert
// doesn't match any of our flight-flagging rules.
function classifyAlert(alert: LatestAlertRow): RuleKey | null {
  if (!alert.trackId) return null;
  if (alert.type === 'GEOFENCE_BREACH') return 'GEOFENCE';
  if (alert.type === 'AIRSPACE_INTRUSION') {
    // CEILING is the only AIRSPACE_INTRUSION subtype operators flagged as
    // an anomaly criterion. TFR_INCURSION and AIRCRAFT_PROXIMITY are still
    // emitted by the ingestion pipeline but stay out of this view until
    // operators ask for them as separate categories.
    //
    // The subtype lives in the alert's metadata blob. listAlerts doesn't
    // currently round-trip that, so we approximate by treating any
    // AIRSPACE_INTRUSION with a recognizable title as a CEILING hit.
    // Refine this once the metadata.subtype is plumbed through the row.
    const title = alert.title.toLowerCase();
    if (title.includes('ceiling') || title.includes('400 ft')) return 'CEILING';
    return null;
  }
  return null;
}

const RULE_LABEL: Record<RuleKey, string> = {
  CEILING: 'Above FAA ceiling',
  GEOFENCE: 'Geofence breach',
  CRITICAL_INFRA: 'Near critical infrastructure',
};

const RULE_ICON: Record<RuleKey, React.ComponentType<{ className?: string }>> = {
  CEILING: ArrowTrendingUpIcon,
  GEOFENCE: MapIcon,
  CRITICAL_INFRA: BuildingOffice2Icon,
};

function severityColor(s: LatestAlertRow['severity']): string {
  switch (s) {
    case 'CRITICAL': return 'bg-red-500/15 text-red-400 border-red-500/30';
    case 'WARNING':  return 'bg-amber-500/15 text-amber-400 border-amber-500/30';
    default:         return 'bg-sky-500/15 text-sky-400 border-sky-500/30';
  }
}

export default function ThreatTab({
  startTime,
  endTime,
  onInvestigateTrack,
}: ThreatTabProps) {
  const [selectedTrack, setSelectedTrack] = useState<string | null>(null);

  // Fetch alerts in the window. Single page of 100 covers almost every
  // realistic period — if a tenant trips more than 100 anomaly alerts in
  // the window we'll wire pagination, but today's caps make that a
  // worst-case "everything is on fire" scenario, not a Tuesday.
  const {
    data: alertsPage,
    isLoading: alertsLoading,
    error: alertsError,
  } = useQuery({
    queryKey: ['threat-tab', 'alerts', startTime, endTime],
    queryFn: () =>
      reportsApi.listAlerts({ from: startTime, to: endTime }, 1, 100),
    staleTime: 60 * 1000,
  });

  // Fetch tracks so we can show "DJI Mavic" instead of "DUMP-abc…" on
  // each card. Capped at the server's pageSize limit (1000).
  const { data: tracksForLabels } = useQuery({
    queryKey: ['threat-tab', 'tracks', startTime, endTime],
    queryFn: async () => {
      const res = await tracksApi.getTracks({ startTime, endTime, pageSize: 1000 });
      return res.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const trackMetaById = useMemo(() => {
    const m = new Map<string, any>();
    for (const t of (tracksForLabels || []) as any[]) {
      const id = t.trackId || t.id;
      if (id) m.set(id, t);
    }
    return m;
  }, [tracksForLabels]);

  // Group matching alerts by trackId and roll up into FlaggedTrack rows.
  const flagged = useMemo<FlaggedTrack[]>(() => {
    const rows = alertsPage?.data ?? [];
    const byTrack = new Map<string, FlaggedTrack>();
    for (const a of rows) {
      if (!a.trackId) continue;
      const rule = classifyAlert(a);
      if (!rule) continue;

      const existing = byTrack.get(a.trackId);
      const hit: RuleHit = {
        rule,
        severity: a.severity,
        title: a.title,
        createdAt: a.createdAt,
        alertId: a.id,
      };
      if (!existing) {
        byTrack.set(a.trackId, {
          trackId: a.trackId,
          hits: [hit],
          topSeverity: a.severity,
          lastSeenIso: a.createdAt,
        });
      } else {
        existing.hits.push(hit);
        if (SEVERITY_RANK[a.severity] > SEVERITY_RANK[existing.topSeverity]) {
          existing.topSeverity = a.severity;
        }
        if (new Date(a.createdAt) > new Date(existing.lastSeenIso)) {
          existing.lastSeenIso = a.createdAt;
        }
      }
    }
    return Array.from(byTrack.values()).sort((a, b) => {
      const sevDiff = SEVERITY_RANK[b.topSeverity] - SEVERITY_RANK[a.topSeverity];
      if (sevDiff !== 0) return sevDiff;
      return new Date(b.lastSeenIso).getTime() - new Date(a.lastSeenIso).getTime();
    });
  }, [alertsPage]);

  const flagCount = flagged.length;
  const criticalCount = flagged.filter((f) => f.topSeverity === 'CRITICAL').length;

  return (
    <div className="space-y-6">
      {/* ───── Criteria banner ───── */}
      <div className="glass-card glow-border p-5">
        <div className="flex items-start gap-3">
          <ShieldExclamationIcon
            className={clsx(
              'h-6 w-6 flex-shrink-0 mt-0.5',
              flagCount > 0 ? 'text-red-400' : 'text-accent',
            )}
          />
          <div className="flex-1">
            <h3 className="text-white font-display font-semibold">
              {alertsLoading
                ? 'Scanning flights…'
                : flagCount > 0
                  ? `${flagCount} flagged flight${flagCount === 1 ? '' : 's'} (${criticalCount} critical)`
                  : 'No flagged flights in this window'}
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Flights are flagged when they match any rule below.
            </p>
            <ul className="mt-3 text-xs text-gray-400 space-y-1.5">
              <li className="flex items-start gap-2">
                <ArrowTrendingUpIcon className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                <span><strong className="text-gray-200">Above FAA ceiling</strong> — altitude exceeds the FAA Facility Map ceiling at the flight's location.</span>
              </li>
              <li className="flex items-start gap-2">
                <MapIcon className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                <span><strong className="text-gray-200">Geofence breach</strong> — flight entered an operator-defined geofence.</span>
              </li>
              <li className="flex items-start gap-2">
                <BuildingOffice2Icon className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
                <span><strong className="text-gray-400">Near critical infrastructure</strong> — <em className="text-gray-500">criteria not yet defined; rule placeholder for follow-up.</em></span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* ───── Loading / error states ───── */}
      {alertsLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      )}

      {alertsError && (
        <div className="glass-card p-6 text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-yellow-400 mx-auto mb-3" />
          <p className="text-sm text-gray-400">
            Failed to load alert data: {(alertsError as Error)?.message}
          </p>
        </div>
      )}

      {/* ───── Flagged-flight list ───── */}
      {!alertsLoading && !alertsError && (
        <div className="space-y-3">
          {flagged.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <InformationCircleIcon className="h-8 w-8 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-400">
                No flights in this window triggered any of the criteria above.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Try a longer time range, or check the Investigation tab to drill into individual tracks directly.
              </p>
            </div>
          ) : (
            flagged.map((f) => (
              <FlaggedCard
                key={f.trackId}
                flagged={f}
                label={deviceLabel(trackMetaById.get(f.trackId))}
                track={trackMetaById.get(f.trackId)}
                isSelected={selectedTrack === f.trackId}
                onSelect={() => setSelectedTrack(f.trackId)}
                onInvestigate={() => onInvestigateTrack(f.trackId)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Flagged-flight card
// ============================================================================

function FlaggedCard({
  flagged,
  label,
  track,
  isSelected,
  onSelect,
  onInvestigate,
}: {
  flagged: FlaggedTrack;
  label: string;
  track: any;
  isSelected: boolean;
  onSelect: () => void;
  onInvestigate: () => void;
}) {
  const sevClass = severityColor(flagged.topSeverity);
  const lastSeen = formatDistanceToNow(new Date(flagged.lastSeenIso), { addSuffix: true });
  const lastSeenAbs = format(new Date(flagged.lastSeenIso), 'MMM d, HH:mm');

  // Unique rule list — same rule firing multiple times collapses to one chip.
  const uniqueRules = Array.from(new Set(flagged.hits.map((h) => h.rule)));

  return (
    <div
      onClick={onSelect}
      className={clsx(
        'glass-card p-4 cursor-pointer transition-all',
        isSelected ? 'ring-1 ring-accent/50 bg-accent/5' : 'hover:bg-navy-800/50',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Device label + severity badge */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-sm font-semibold text-white truncate">
              {label}
            </span>
            <span className={clsx(
              'flex-shrink-0 px-1.5 py-0.5 text-[10px] font-bold rounded border',
              sevClass,
            )}>
              {flagged.topSeverity}
            </span>
            {track?.serialNumber && (
              <span className="text-[11px] font-mono text-gray-500">
                {String(track.serialNumber).slice(-8)}
              </span>
            )}
          </div>

          {/* Track id — secondary */}
          <div className="text-[11px] font-mono text-gray-500 truncate mb-2">
            {flagged.trackId}
          </div>

          {/* Rule chips */}
          <div className="flex flex-wrap gap-1.5 mb-2">
            {uniqueRules.map((r) => {
              const Icon = RULE_ICON[r];
              return (
                <span
                  key={r}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full bg-navy-700 text-gray-200 border border-navy-600"
                >
                  <Icon className="h-3 w-3" />
                  {RULE_LABEL[r]}
                </span>
              );
            })}
          </div>

          {/* Last-seen line */}
          <div className="text-[11px] text-gray-500" title={lastSeenAbs}>
            Last alert {lastSeen} · {flagged.hits.length} hit{flagged.hits.length === 1 ? '' : 's'} total
          </div>
        </div>

        {/* Investigate button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onInvestigate();
          }}
          className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-accent hover:text-white hover:bg-accent/20 rounded-md transition-colors"
        >
          Investigate &rarr;
        </button>
      </div>
    </div>
  );
}
